// The pure parts of the operating-cadence screens (MEETINGS): the issues board's columns, labels
// and drop maths, the agenda's sections and timers, the weekly schedule as an RRULE, and the
// dates the screens show. No React, no fetching: cadence-model.test.ts covers it.
import type { AgendaItemView, IssueView, MeetingDetail } from "../../api/types";

// Issues ---------------------------------------------------------------------------------------

export const ISSUE_COLUMNS = ["identify", "discuss", "solve"] as const;
export type IssueColumn = (typeof ISSUE_COLUMNS)[number];

export const COLUMN_LABELS: Record<IssueColumn, string> = {
  identify: "Identify",
  discuss: "Discuss",
  solve: "Solve",
};
export const COLUMN_HINTS: Record<IssueColumn, string> = {
  identify: "Name the real problem",
  discuss: "Talk it through",
  solve: "Decide what happens",
};

export const PRIORITY_LABELS: Record<number, string> = { 1: "High", 2: "Normal", 3: "Low" };
export const PRIORITY_BADGE: Record<number, "danger" | "neutral" | "info"> = {
  1: "danger",
  2: "neutral",
  3: "info",
};

/** "4d", "0d" for today; the board's age chip. */
export const ageLabel = (days: number) => `${Math.max(0, days)}d`;

/** "Recruiting lead · Jordan Lee", or the one that is known. */
export function ownerLabel(i: Pick<IssueView, "owner_title" | "owner_name">): string | null {
  if (i.owner_title && i.owner_name) return `${i.owner_title} · ${i.owner_name}`;
  return i.owner_title ?? i.owner_name ?? null;
}

/** The board's columns, each in position order (the server's order is kept within a column). */
export function columnsOf(issues: readonly IssueView[]): Record<IssueColumn, IssueView[]> {
  const out: Record<IssueColumn, IssueView[]> = { identify: [], discuss: [], solve: [] };
  for (const i of issues)
    if ((ISSUE_COLUMNS as readonly string[]).includes(i.status) && !i.archived_at)
      out[i.status as IssueColumn].push(i);
  for (const k of ISSUE_COLUMNS) out[k].sort((a, b) => a.position - b.position);
  return out;
}

/**
 * The board after `id` is dropped into `to` at `index` (an index in the target column without
 * the moved card): the moved card gets `index`, both columns are renumbered. The cache shows
 * this before the server answers; the server renumbers the same way.
 */
export function moveIssue(
  issues: readonly IssueView[],
  id: string,
  to: IssueColumn,
  index: number,
): IssueView[] {
  const moved = issues.find((i) => i.id === id);
  if (!moved) return [...issues];
  const from = moved.status;
  const column = issues
    .filter((i) => i.id !== id && i.status === to && !i.archived_at)
    .sort((a, b) => a.position - b.position);
  const at = Math.max(0, Math.min(index, column.length));
  const next = { ...moved, status: to, position: at, resolved_at: null };
  column.splice(at, 0, next);
  const positions = new Map(column.map((i, n) => [i.id, n]));
  if (from !== to)
    issues
      .filter((i) => i.id !== id && i.status === from && !i.archived_at)
      .sort((a, b) => a.position - b.position)
      .forEach((i, n) => {
        positions.set(i.id, n);
      });
  return issues.map((i) => {
    if (i.id === id) return { ...next, position: positions.get(id) ?? at };
    const p = positions.get(i.id);
    return p === undefined ? i : { ...i, position: p };
  });
}

/** `YYYY-MM-DD` of a date in the browser's calendar (a date input's value). */
export function dateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The default due date of a to-do: 7 days from `now`, as a date input's value. */
export const defaultDue = (now = new Date()) => dateInput(new Date(now.getTime() + 7 * 86_400_000));

/** A date input's value as the instant to send: noon local time (never the day before). */
export function dueInstant(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d, 12).toISOString();
}

// Agenda ---------------------------------------------------------------------------------------

export const AGENDA_ORDER = [
  "scorecard",
  "priorities",
  "issues",
  "todos",
  "headlines",
  "custom",
] as const;
export type AgendaSection = (typeof AGENDA_ORDER)[number];

export const SECTION_LABELS: Record<AgendaSection, string> = {
  scorecard: "Scorecard misses",
  priorities: "Off-track priorities",
  issues: "Issues",
  todos: "To-dos",
  headlines: "Headlines",
  custom: "Custom",
};

/** Seconds an item has run: its stored time, plus the running time when it is the live one. */
export function itemElapsed(
  item: Pick<AgendaItemView, "id" | "elapsed_s">,
  meeting: Pick<MeetingDetail["meeting"], "state" | "current_item_id" | "item_started_at">,
  now: number,
): number {
  const running =
    meeting.state === "live" && meeting.current_item_id === item.id && meeting.item_started_at
      ? Math.max(0, Math.floor((now - Date.parse(meeting.item_started_at)) / 1000))
      : 0;
  return item.elapsed_s + running;
}

export type TimerTone = "ok" | "over" | "late";

/** Within the box: ok; over it: amber; more than a quarter over: red. */
export function timerTone(spent: number, box: number): TimerTone {
  if (box <= 0 || spent <= box) return "ok";
  return spent > box * 1.25 ? "late" : "over";
}

export type AgendaGroup = {
  section: AgendaSection;
  label: string;
  items: AgendaItemView[];
  box: number;
  spent: number;
  tone: TimerTone;
};

/** The agenda by section, in the builder's order, with each section's time box and time spent. */
export function groupAgenda(
  agenda: readonly AgendaItemView[],
  meeting: Pick<MeetingDetail["meeting"], "state" | "current_item_id" | "item_started_at">,
  now: number,
): AgendaGroup[] {
  const out: AgendaGroup[] = [];
  for (const section of AGENDA_ORDER) {
    const items = agenda
      .filter((i) => i.section === section)
      .sort((a, b) => a.position - b.position);
    if (!items.length) continue;
    const box = items.reduce((n, i) => n + i.time_box_s, 0);
    const spent = items.reduce((n, i) => n + itemElapsed(i, meeting, now), 0);
    out.push({
      section,
      label: SECTION_LABELS[section],
      items,
      box,
      spent,
      tone: timerTone(spent, box),
    });
  }
  return out;
}

/** "4:05", "1:02:05"; a timer's face. */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
}

/** "5 min", "1 h 30 min"; a time box. */
export function minutes(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  return m % 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m / 60} h`;
}

// Schedules ------------------------------------------------------------------------------------

export const WEEKDAYS = [
  ["MO", "Monday"],
  ["TU", "Tuesday"],
  ["WE", "Wednesday"],
  ["TH", "Thursday"],
  ["FR", "Friday"],
  ["SA", "Saturday"],
  ["SU", "Sunday"],
] as const;
export type Weekday = (typeof WEEKDAYS)[number][0];

export type WeeklySlot = { weekday: Weekday; hour: number; minute: number };

/** `{TU, 10, 0}` → `FREQ=WEEKLY;BYDAY=TU;BYHOUR=10;BYMINUTE=0` (the company's time zone). */
export function weeklyRule(slot: WeeklySlot): string {
  return `FREQ=WEEKLY;BYDAY=${slot.weekday};BYHOUR=${slot.hour};BYMINUTE=${slot.minute}`;
}

/** The weekly slot of a rule this screen wrote (null for any other rule). */
export function parseWeekly(rule: string): WeeklySlot | null {
  const parts = new Map(
    rule
      .replace(/^RRULE:/, "")
      .split(";")
      .map((p) => p.split("=") as [string, string]),
  );
  if (parts.get("FREQ") !== "WEEKLY") return null;
  const day = parts.get("BYDAY");
  if (!day || !WEEKDAYS.some(([k]) => k === day)) return null;
  const hour = Number(parts.get("BYHOUR") ?? 0);
  const minute = Number(parts.get("BYMINUTE") ?? 0);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null;
  return { weekday: day as Weekday, hour, minute };
}

/** "Tuesdays at 10:00"; the rule itself when it is not a plain weekly slot. */
export function describeSchedule(rule: string): string {
  const slot = parseWeekly(rule);
  if (!slot) return rule;
  const day = WEEKDAYS.find(([k]) => k === slot.weekday)?.[1] ?? slot.weekday;
  return `${day}s at ${String(slot.hour).padStart(2, "0")}:${String(slot.minute).padStart(2, "0")}`;
}

/** "Tue, Oct 27 · 10:00" in the company's time zone (the schedule's), else the browser's. */
export function meetingWhen(iso: string, timeZone?: string | null): string {
  const d = new Date(iso);
  const zone = timeZone ? { timeZone } : {};
  const day = d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...zone,
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    ...zone,
  });
  return `${day} · ${time}`;
}

export const MEETING_STATE: Record<
  MeetingDetail["meeting"]["state"],
  { label: string; badge: "accent" | "success" | "neutral" | "warning" }
> = {
  scheduled: { label: "Scheduled", badge: "accent" },
  live: { label: "Live", badge: "success" },
  ended: { label: "Ended", badge: "neutral" },
  cancelled: { label: "Cancelled", badge: "warning" },
};

export const PREREAD_STATE: Record<
  MeetingDetail["preread"]["state"],
  { label: string; badge: "neutral" | "info" | "warning" | "success" }
> = {
  none: { label: "Not asked", badge: "neutral" },
  requested: { label: "Requested", badge: "info" },
  pending: { label: "Pending", badge: "warning" },
  ready: { label: "Ready", badge: "success" },
};

/** "Placements · W42: 3 of 5" — the line an issue raised from a missed KPI shows. */
export function metricLabel(name: string, week: string, actual: string, target: string): string {
  return `${name} · W${week.slice(6)}: ${actual} of ${target}`;
}
